// Modeled §401(a)(4) general test (cross-testing) for defined-contribution
// allocations, per 26 CFR 1.401(a)(4)-2, -3, -8. This is what makes an
// owner-heavy "new comparability" profit-sharing design defensible: the employer
// nonelective/profit-sharing allocations are converted to Equivalent Benefit
// Accrual Rates (EBARs) and tested on a benefits basis.
//
// Steps:
//   1. Minimum allocation gateway: every NHCE must get an allocation rate of at
//      least the lesser of 5% or one-third of the highest HCE allocation rate.
//   2. EBAR for each employee: allocation projected to the testing age at the
//      standard interest rate, annuitized, expressed as a percent of pay.
//   3. Rate groups: each HCE's rate group is every (non-excludable) employee whose
//      EBAR is >= that HCE's EBAR. Each rate group must satisfy §410(b) — either a
//      ratio percentage >= 70%, OR a ratio percentage >= the classification
//      midpoint together with the EBAR-based average-benefit-percentage test.
//
// MODELED, not a formal test. It uses documented interest/mortality assumptions
// (see ./constants.js), the allocation the app computes, and cannot verify the
// reasonableness of the plan's classification, related-employer aggregation, a
// combined DB/DC benefits-basis test, imputed permitted disparity, or the plan's
// actual actuarial elections. It is not a substitute for the plan's actuary/TPA.

import {
  CROSS_TEST_INTEREST_RATE,
  CROSS_TEST_TESTING_AGE,
  CROSS_TEST_ANNUITY_FACTOR_MONTHLY,
  GATEWAY_MAX_PERCENT,
  GATEWAY_FRACTION_OF_HIGHEST_HCE,
  PERMITTED_DISPARITY_FACTOR,
  TAXABLE_WAGE_BASE_BY_YEAR,
  RATE_BAND_FRACTION,
  RATE_BAND_ADDITIVE_PP,
} from './constants.js'

// Rate-group band FLOOR for an HCE at benefits-basis rate `rate` (percent of
// pay), placing the HCE at the top of the most favorable band. Uses the more
// inclusive of the multiplicative ±5% and additive ±(1/20)pp rules.
export function rateBandFloor(rate) {
  const h = Math.max(0, Number(rate) || 0)
  const multiplicative = h * (1 - RATE_BAND_FRACTION) / (1 + RATE_BAND_FRACTION)
  const additive = h - 2 * RATE_BAND_ADDITIVE_PP
  return Math.min(multiplicative, additive)
}
import { classificationSafeHarborPercent, classificationUnsafePercent } from './coverage.js'
import {
  accumulationFactorForAges,
  getAgeWeightedAssumptionSet,
  monthlyAnnuityFactorAtAge,
} from './ageWeightedAssumptions.js'

const EBAR_TOLERANCE = 1e-6

// Growth factor from the employee's age to the testing age at the standard rate.
export function accumulationFactor(age, {
  testingAge = CROSS_TEST_TESTING_AGE,
  interest = CROSS_TEST_INTEREST_RATE,
} = {}) {
  const years = Math.max(0, (Number(testingAge) || 0) - (Number(age) || 0))
  return Math.pow(1 + interest, years)
}

// EBAR as an annual benefit percent of compensation. The annuity factor cancels
// out of every ratio-based decision below; it only scales the displayed value.
export function computeEbar(allocation, comp, age, options = {}) {
  const compensation = Number(comp) || 0
  if (compensation <= 0) return 0
  const alloc = Math.max(0, Number(allocation) || 0)
  const assumptionSet = options.assumptionSet
  const testingAge = Number(options.testingAge ?? CROSS_TEST_TESTING_AGE)
  const accumulatedFactor = assumptionSet
    ? accumulationFactorForAges(age, testingAge, assumptionSet)
    : accumulationFactor(age, options)
  const annuityFactor = assumptionSet
    ? monthlyAnnuityFactorAtAge(testingAge, assumptionSet)
    : CROSS_TEST_ANNUITY_FACTOR_MONTHLY
  if (!(accumulatedFactor > 0) || !(annuityFactor > 0)) return 0
  const accumulated = alloc * accumulatedFactor
  const annualAnnuity = (accumulated / annuityFactor) * 12
  return (annualAnnuity / compensation) * 100
}

// The minimum allocation gateway rate (percent): lesser of 5% or 1/3 of the
// highest HCE allocation rate.
export function minimumAllocationGatewayRate(highestHceAllocationRatePercent) {
  const highest = Math.max(0, Number(highestHceAllocationRatePercent) || 0)
  return Math.min(GATEWAY_MAX_PERCENT, highest * GATEWAY_FRACTION_OF_HIGHEST_HCE)
}

// Both minimum-gateway measurements, calculated explicitly and separately
// (1.401(a)(4)-8(b)(1)(vi)):
//   - one-third path: each benefiting NHCE's allocation rate must be at least
//     one third of the highest HCE allocation rate;
//   - 5% deemed-satisfaction path: each benefiting NHCE's allocation dollars
//     must be at least 5% of §415(c)(3) compensation.
// Rates on BOTH paths are measured on §415(c)(3) compensation (the audited
// IRS EP CPE position; a plan-comp rate can overstate the true gateway rate).
// Each row may carry its own basis per path (`gatewayOneThirdComp`,
// `gatewayFivePercentComp`), defaulting to `gatewayComp`, so a plan-supplied
// definition is representable — there is no single hard-wired comp field.
//
// The operative requirement is the lesser of the general one-third rule and
// the 5% deemed-satisfaction alternative. A plan may choose to allocate 5%,
// but 5% is not a second gateway method that can make an otherwise-valid
// one-third result fail. Nonbenefiting NHCEs are a §410(b) coverage question,
// never a gateway shortfall.
export function evaluateMinimumGateway(rows = [], { gatewayExempt = false } = {}) {
  const operativeMethod = 'lesser-of'
  const hces = rows.filter((row) => row.isHCE)
  const nhces = rows.filter((row) => !row.isHCE)
  const oneThirdComp = (row) => Number(row.gatewayOneThirdComp) > 0
    ? Number(row.gatewayOneThirdComp)
    : (Number(row.gatewayComp) > 0 ? Number(row.gatewayComp) : Number(row.comp) || 0)
  const fivePercentComp = (row) => Number(row.gatewayFivePercentComp) > 0
    ? Number(row.gatewayFivePercentComp)
    : (Number(row.gatewayComp) > 0 ? Number(row.gatewayComp) : Number(row.comp) || 0)

  const highestHceRate = hces.reduce((max, row) => {
    const comp = oneThirdComp(row)
    return comp > 0 ? Math.max(max, (Math.max(0, Number(row.allocation) || 0) / comp) * 100) : max
  }, 0)
  const oneThirdRequiredRate = highestHceRate * GATEWAY_FRACTION_OF_HIGHEST_HCE

  const perPerson = []
  let shortfallCount = 0
  let shortfallTotal = 0
  for (const nhce of nhces) {
    const benefiting = (Number(nhce.allocation) || 0) > 0
    if (!benefiting) {
      perPerson.push({
        id: nhce.id ?? null, benefiting: false,
        requiredDollars: 0, suppliedDollars: 0, shortfallDollars: 0,
      })
      continue
    }
    const supplied = Math.max(0, Number(nhce.allocation) || 0)
    const oneThirdRequired = gatewayExempt ? 0 : (oneThirdRequiredRate / 100) * oneThirdComp(nhce)
    const fivePercentRequired = gatewayExempt ? 0 : (GATEWAY_MAX_PERCENT / 100) * fivePercentComp(nhce)
    const required = Math.min(oneThirdRequired, fivePercentRequired)
    const shortfall = Math.max(0, required - supplied)
    if (shortfall > 1e-6) {
      shortfallCount += 1
      shortfallTotal += shortfall
    }
    perPerson.push({
      id: nhce.id ?? null,
      benefiting: true,
      oneThirdRequiredDollars: oneThirdRequired,
      fivePercentRequiredDollars: fivePercentRequired,
      requiredDollars: required,
      suppliedDollars: supplied,
      shortfallDollars: shortfall,
    })
  }
  return {
    method: operativeMethod,
    gatewayExempt: gatewayExempt === true,
    highestHceRate,
    oneThirdRequiredRate,
    fivePercentRate: GATEWAY_MAX_PERCENT,
    operativeRate: gatewayExempt ? 0 : Math.min(GATEWAY_MAX_PERCENT, oneThirdRequiredRate),
    perPerson,
    shortfallCount,
    shortfallTotal,
    satisfied: shortfallCount === 0,
    benefitingNhceCount: perPerson.filter((row) => row.benefiting).length,
    nonBenefitingNhceCount: perPerson.filter((row) => !row.benefiting).length,
  }
}

// Imputed permitted disparity (§1.401(a)(4)-7(b)): the factor by which an
// employee's unadjusted allocation rate (and hence EBAR, which is linear in the
// allocation) is scaled. The adjusted rate is the lesser of twice the
// unadjusted rate, or the rate plus permitted disparity on the integration
// level (taxable wage base): min( 2*AR, (A + 5.7% * min(C, TWB)) / C ). Never
// applied to the gateway. Returns 1 (no adjustment) for a zero allocation.
export function permittedDisparityScale(allocation, comp, taxableWageBase) {
  const alloc = Math.max(0, Number(allocation) || 0)
  const compensation = Number(comp) || 0
  const twb = Number(taxableWageBase) || 0
  if (!(alloc > 0) || !(compensation > 0) || !(twb > 0)) return 1
  const unadjustedRate = alloc / compensation
  const excessRate = (alloc + PERMITTED_DISPARITY_FACTOR * Math.min(compensation, twb)) / compensation
  const adjustedRate = Math.min(2 * unadjustedRate, excessRate)
  return adjustedRate / unadjustedRate
}

// Evaluate the full DC general test. `participants` are objects:
//   { id, isHCE, excludable, allocation, abptAllocation, comp, gatewayComp, age }
// where `allocation` is the employer NONELECTIVE + profit-sharing (plus any
// reallocated forfeiture) dollars being cross-tested (match and elective
// deferrals are tested under ACP/ADP, not in the rate groups).
// `abptAllocation` is the broader §1.410(b)-5 amount for the average benefit
// percentage test — ALL employer-provided benefits, including elective
// deferrals and match but NOT catch-ups; it defaults to `allocation` when
// omitted, which understates HCE averages and makes the ABPT
// easier than the real test, so callers should pass it. `gatewayComp` is
// §415(c)(3) compensation — the default basis for BOTH gateway measurements
// (1.401(a)(4)-8(b)(1)(vi); see evaluateMinimumGateway, which also accepts
// per-path bases); defaults to `comp` when omitted.
// The statutory operative result is always the lesser-of calculation above.
export function evaluateCrossTesting(participants = [], options = {}) {
  if (options.assumptionSet && !getAgeWeightedAssumptionSet(options.assumptionSet)) {
    return { available: false, status: 'unavailable', reason: 'unsupported-assumption-set' }
  }
  if ((Array.isArray(participants) ? participants : []).some((participant) => (
    participant && !participant.excludable
    && (participant.age === null || participant.age === undefined || participant.age === '' || !Number.isFinite(Number(participant.age)))
  ))) {
    return { available: false, status: 'unavailable', reason: 'missing-age' }
  }
  const allParticipants = Array.isArray(participants) ? participants : []
  // Otherwise-excludable disaggregation (§1.410(b)-7(c)(3)): excludable rows are
  // carved out of this (the statutory-eligible) component plan. They form their
  // own group that must satisfy §410(b) separately; a group of only NHCEs does so
  // trivially, but this is disclosed rather than assumed away.
  const excludedNhceCount = allParticipants.filter((p) => p && p.excludable && !p.isHCE && Number(p.comp) > 0).length
  const excludedHceCount = allParticipants.filter((p) => p && p.excludable && p.isHCE && Number(p.comp) > 0).length
  const imputeDisparity = options.imputePermittedDisparity === true
  const taxableWageBase = TAXABLE_WAGE_BASE_BY_YEAR[options.planYear]
    ?? TAXABLE_WAGE_BASE_BY_YEAR[Math.max(...Object.keys(TAXABLE_WAGE_BASE_BY_YEAR).map(Number))]
  const rows = allParticipants
    .filter((p) => p && !p.excludable && Number(p.comp) > 0)
    .map((p) => {
      const allocationRate = (Math.max(0, Number(p.allocation) || 0) / Number(p.comp)) * 100
      const ebarOptions = {
        ...options,
        testingAge: p.testingAge ?? options.testingAge,
        assumptionSet: p.assumptionSet ?? options.assumptionSet,
      }
      const abptAllocation = p.abptAllocation ?? p.allocation
      const gatewayComp = Number(p.gatewayComp) > 0 ? Number(p.gatewayComp) : Number(p.comp)
      const ebar = computeEbar(p.allocation, p.comp, p.age, ebarOptions)
      const abptEbar = computeEbar(abptAllocation, p.comp, p.age, ebarOptions)
      // Imputed permitted disparity scales the benefits-basis rates used for the
      // rate groups and ABPT; the gateway (gatewayRate) always stays unadjusted.
      const disparityScale = imputeDisparity ? permittedDisparityScale(p.allocation, p.comp, taxableWageBase) : 1
      const abptDisparityScale = imputeDisparity ? permittedDisparityScale(abptAllocation, p.comp, taxableWageBase) : 1
      return {
        id: p.id ?? null,
        isHCE: Boolean(p.isHCE),
        comp: Number(p.comp),
        gatewayComp,
        gatewayOneThirdComp: Number(p.gatewayOneThirdComp) > 0 ? Number(p.gatewayOneThirdComp) : undefined,
        gatewayFivePercentComp: Number(p.gatewayFivePercentComp) > 0 ? Number(p.gatewayFivePercentComp) : undefined,
        allocation: Math.max(0, Number(p.allocation) || 0),
        allocationRate,
        gatewayRate: (Math.max(0, Number(p.allocation) || 0) / gatewayComp) * 100,
        benefiting: (Number(p.allocation) || 0) > 0,
        testingAge: p.testingAge ?? options.testingAge ?? CROSS_TEST_TESTING_AGE,
        ebar,
        abptEbar,
        rgEbar: ebar * disparityScale,
        rgAbptEbar: abptEbar * abptDisparityScale,
      }
    })

  const hces = rows.filter((r) => r.isHCE)
  const nhces = rows.filter((r) => !r.isHCE)
  const available = hces.length > 0 && nhces.length > 0
  if (!available) {
    return {
      available: false,
      status: 'unavailable',
      reason: hces.length === 0 ? 'no-hce' : 'no-nhce',
    }
  }

  // ---- Minimum allocation gateway (both paths, document method operative) ----
  // Benefiting NHCEs only: an NHCE who receives nothing under the
  // nonelective/PS component is a §410(b) coverage question, not a gateway
  // shortfall. Their count is surfaced so the coverage concern stays visible.
  const gateway = evaluateMinimumGateway(rows, {
    gatewayExempt: options.gatewayExempt === true,
  })
  const highestHceAllocationRate = gateway.highestHceRate
  const gatewayRate = gateway.gatewayExempt ? 0 : gateway.operativeRate
  const gatewayShortfallCount = gateway.shortfallCount
  const gatewayShortfallTotal = gateway.shortfallTotal
  const baseGatewaySatisfied = gateway.satisfied
  const nonBenefitingNhceCount = gateway.nonBenefitingNhceCount

  // ---- Plan §410(b) ratio percentage (from the full nonexcludable population) ----
  const benefitingHceCount = hces.filter((r) => r.benefiting).length
  const benefitingNhceCount = nhces.filter((r) => r.benefiting).length
  const hceBenefitingRate = hces.length ? (benefitingHceCount / hces.length) * 100 : 0
  const nhceBenefitingRate = nhces.length ? (benefitingNhceCount / nhces.length) * 100 : 0
  const planRatioPercent = hceBenefitingRate > 0
    ? (nhceBenefitingRate / hceBenefitingRate) * 100
    : (nhceBenefitingRate > 0 ? 100 : 0)

  // ---- Classification midpoint (for rate groups using the average benefit route) ----
  const concentrationPercent = (nhces.length / rows.length) * 100
  const midpointPercent = (classificationSafeHarborPercent(concentrationPercent)
    + classificationUnsafePercent(concentrationPercent)) / 2
  // 1.401(a)(4)-2(c)(3)(ii): a rate group's classification component is deemed
  // satisfied at the LESSER of the midpoint and the plan's own ratio
  // percentage — a plan that already covers narrowly can't be held to a higher
  // bar than the plan itself passes.
  const classificationThresholdPercent = Math.min(midpointPercent, planRatioPercent)
  const classificationThresholdBasis = planRatioPercent < midpointPercent
    ? 'plan-ratio-percentage' : 'midpoint'

  // ---- EBAR-based average benefit percentage test (a rate group may rely on it) ----
  // Uses the broader abptEbar (all employer-provided benefits incl. deferrals),
  // disparity-adjusted when imputed permitted disparity is elected.
  const avg = (arr) => (arr.length ? arr.reduce((s, r) => s + r.rgAbptEbar, 0) / arr.length : 0)
  const nhceAvgEbar = avg(nhces)
  const hceAvgEbar = avg(hces)
  const abptRatio = hceAvgEbar > 0 ? (nhceAvgEbar / hceAvgEbar) * 100 : (nhceAvgEbar > 0 ? 100 : 0)
  const abptPass = abptRatio >= 70

  // ---- Rate groups: one per HCE ----
  // Rate groups use the benefits-basis rate (rgEbar), which carries any imputed
  // permitted disparity. With rate banding elected, bands are EXPLICIT and
  // NONOVERLAPPING: anchors run from the highest HCE rate down, and a lower
  // HCE whose rate already falls inside an existing band shares that band
  // rather than wrapping its own most-favorable band around itself. Everyone
  // whose rate falls within a band is treated as at that band's rate. The
  // anti-abuse rule (HCE rates within a band not significantly higher than
  // NHCE rates) is facts-and-circumstances; the widest HCE-over-banded-NHCE
  // spread is surfaced so a reviewer can confirm the grouping is not abusive.
  const applyBanding = options.rateBanding === true
  const rateBands = []
  if (applyBanding) {
    const anchorRates = hces.map((r) => r.rgEbar).sort((a, b) => b - a)
    for (const rate of anchorRates) {
      const shared = rateBands.some((band) => rate >= band.floor - EBAR_TOLERANCE && rate <= band.ceiling + EBAR_TOLERANCE)
      if (!shared) rateBands.push({ floor: rateBandFloor(rate), ceiling: rate })
    }
    for (const band of rateBands) {
      band.hceCount = hces.filter((r) => r.rgEbar >= band.floor - EBAR_TOLERANCE && r.rgEbar <= band.ceiling + EBAR_TOLERANCE).length
      band.nhceCount = nhces.filter((r) => r.rgEbar >= band.floor - EBAR_TOLERANCE && r.rgEbar <= band.ceiling + EBAR_TOLERANCE).length
    }
  }
  const bandFor = (rate) => rateBands.find((band) => rate >= band.floor - EBAR_TOLERANCE && rate <= band.ceiling + EBAR_TOLERANCE) || null
  let maxBandedHceOverNhceSpread = 0
  const rateGroups = hces.map((hce) => {
    const band = applyBanding ? bandFor(hce.rgEbar) : null
    const bandFloor = band ? band.floor : hce.rgEbar
    const threshold = bandFloor - EBAR_TOLERANCE
    const hceInGroup = hces.filter((r) => r.rgEbar >= threshold).length
    const bandedNhces = nhces.filter((r) => r.rgEbar >= threshold)
    const nhceInGroup = bandedNhces.length
    if (applyBanding && nhceInGroup > 0) {
      const lowestBandedNhce = Math.min(...bandedNhces.map((r) => r.rgEbar))
      if (lowestBandedNhce > 0) {
        maxBandedHceOverNhceSpread = Math.max(
          maxBandedHceOverNhceSpread,
          (hce.rgEbar - lowestBandedNhce) / lowestBandedNhce,
        )
      }
    }
    const hceCoverage = (hceInGroup / hces.length) * 100
    const nhceCoverage = (nhceInGroup / nhces.length) * 100
    const ratioPercent = hceCoverage > 0 ? (nhceCoverage / hceCoverage) * 100 : (nhceCoverage > 0 ? 100 : 0)
    const passesRatio = ratioPercent >= 70
    const passesAverageBenefit = ratioPercent >= classificationThresholdPercent && abptPass
    return {
      hceId: hce.id,
      ebar: hce.ebar,
      rateGroupEbar: hce.rgEbar,
      bandFloor: applyBanding ? bandFloor : null,
      hceCount: hceInGroup,
      nhceCount: nhceInGroup,
      ratioPercent,
      thresholdPercent: classificationThresholdPercent,
      thresholdBasis: classificationThresholdBasis,
      passesRatio,
      passesAverageBenefit,
      passes: passesRatio || passesAverageBenefit,
      basis: passesRatio ? 'ratio' : (passesAverageBenefit ? 'average-benefit' : 'none'),
    }
  })
  const allRateGroupsPass = rateGroups.every((g) => g.passes)

  const baseGeneralTestPasses = allRateGroupsPass
  const excludedParticipants = options.otherwiseExcludableApplied === true
    ? allParticipants.filter((participant) => participant?.excludable && Number(participant.comp) > 0)
    : []
  let otherwiseExcludableTest = {
    available: true,
    status: 'not-applicable',
    passes: true,
    gatewaySatisfied: true,
    generalTestPasses: true,
    hceCount: excludedHceCount,
    nhceCount: excludedNhceCount,
  }
  if (excludedParticipants.length > 0 && excludedHceCount === 0) {
    otherwiseExcludableTest = {
      available: true,
      status: 'passes-no-hce',
      passes: true,
      gatewaySatisfied: true,
      generalTestPasses: true,
      hceCount: 0,
      nhceCount: excludedNhceCount,
    }
  } else if (excludedParticipants.length > 0 && excludedNhceCount === 0) {
    otherwiseExcludableTest = {
      available: true,
      status: 'fails-no-nhce',
      passes: false,
      gatewaySatisfied: true,
      generalTestPasses: false,
      hceCount: excludedHceCount,
      nhceCount: 0,
    }
  } else if (excludedParticipants.length > 0) {
    const result = evaluateCrossTesting(
      excludedParticipants.map((participant) => ({ ...participant, excludable: false })),
      { ...options, otherwiseExcludableApplied: false },
    )
    otherwiseExcludableTest = {
      available: result.available,
      status: result.status,
      passes: result.available && result.gatewaySatisfied && result.generalTestPasses,
      gatewaySatisfied: result.gatewaySatisfied,
      generalTestPasses: result.generalTestPasses,
      hceCount: excludedHceCount,
      nhceCount: excludedNhceCount,
      result,
    }
  }
  const availableWithDisaggregation = otherwiseExcludableTest.available
  const gatewaySatisfied = baseGatewaySatisfied
    && otherwiseExcludableTest.gatewaySatisfied
  const generalTestPasses = baseGeneralTestPasses && otherwiseExcludableTest.generalTestPasses
  const status = !availableWithDisaggregation
    ? 'unavailable'
    : !gatewaySatisfied ? 'gateway-not-met'
      : generalTestPasses ? 'passes' : 'fails'

  return {
    available: availableWithDisaggregation,
    reason: availableWithDisaggregation
      ? null
      : (otherwiseExcludableTest.result?.reason || 'otherwise-excludable-test-unavailable'),
    testingAge: options.testingAge ?? CROSS_TEST_TESTING_AGE,
    assumptionSet: options.assumptionSet || null,
    interest: options.interest ?? CROSS_TEST_INTEREST_RATE,
    gatewayRate,
    gatewayExempt: options.gatewayExempt || null,
    gatewayRoute: options.gatewayRoute || 'gateway',
    otherwiseExcludableApplied: options.otherwiseExcludableApplied === true,
    excludedNhceCount,
    excludedHceCount,
    otherwiseExcludableTest,
    imputedPermittedDisparityApplied: imputeDisparity,
    rateBandingApplied: applyBanding,
    // Widest HCE-over-banded-NHCE rate spread (fraction) across all rate groups;
    // a large value is the anti-abuse "significantly higher" signal a reviewer
    // must weigh before relying on grouping. Null when banding is off.
    maxBandedHceOverNhceSpread: applyBanding ? maxBandedHceOverNhceSpread : null,
    // Optional TPA elections that were modeled here are surfaced so a passing
    // result never looks unconditional. Each is a documented approximation the
    // plan's TPA/actuary must confirm is available for this design.
    modeledElections: [
      imputeDisparity ? { election: 'imputed-permitted-disparity', citation: '26 CFR 1.401(a)(4)-7', taxableWageBase, disparityFactor: PERMITTED_DISPARITY_FACTOR, estimated: true } : null,
      applyBanding ? { election: 'rate-banding', citation: '26 CFR 1.401(a)(4)-2(c)(2)(v), -3(d)(3)(ii)', bandFraction: RATE_BAND_FRACTION, additivePp: RATE_BAND_ADDITIVE_PP, maxHceOverNhceSpread: maxBandedHceOverNhceSpread, antiAbuseNote: 'Grouping is barred where HCE rates within a band are significantly higher than NHCE rates in it (facts and circumstances).', estimated: true } : null,
      options.gatewayExempt ? { election: `non-gateway-route:${options.gatewayRoute || 'age-service-schedule'}`, citation: '26 CFR 1.401(a)(4)-8(b)(1)', estimated: true } : null,
      options.otherwiseExcludableApplied ? { election: 'otherwise-excludable-disaggregation', citation: '26 CFR 1.410(b)-7(c)(3)', excludedNhceCount, excludedHceCount, estimated: true } : null,
    ].filter(Boolean),
    // Honest scope: aspects this modeled screen does not evaluate.
    limitations: [
      'Compensation is taken as entered; the §414(s) compensation-ratio test for a non-safe-harbor definition is not performed.',
      'Related-employer (controlled-group / affiliated-service-group) aggregation is not determined.',
      'Restructuring into component plans (§1.401(a)(4)-9) is not modeled.',
    ],
    highestHceAllocationRate,
    gatewayShortfallCount,
    gatewayShortfallTotal,
    gatewaySatisfied,
    nonBenefitingNhceCount,
    // Full dual-path gateway detail: per-person required/supplied/shortfall
    // dollars on both the one-third and 5% measurements, and which document
    // method produced the operative result.
    gateway,
    gatewayMinimumMethod: gateway.method,
    concentrationPercent,
    midpointPercent,
    planRatioPercent,
    classificationThresholdPercent,
    classificationThresholdBasis,
    // Nonexcludable population preserved with counts and non-benefiting ids so
    // nobody silently disappears from coverage or the ABPT.
    population: {
      nonexcludableHceCount: hces.length,
      nonexcludableNhceCount: nhces.length,
      benefitingHceCount,
      benefitingNhceCount,
      nonBenefitingNhceIds: nhces.filter((r) => !r.benefiting).map((r) => r.id),
      excludedHceCount,
      excludedNhceCount,
    },
    rateBands: applyBanding ? rateBands : null,
    nhceAvgEbar,
    hceAvgEbar,
    abptRatio,
    abptPass,
    rateGroups,
    failingRateGroupCount: rateGroups.filter((g) => !g.passes).length,
    allRateGroupsPass,
    generalTestPasses,
    status,
    // Per-person testing detail (worksheet rows): { id, isHCE, comp,
    // allocationRate, ebar, abptEbar, testingAge }.
    rows,
  }
}
